// Typed wrappers around the deployed Cloud Function callables.
// Centralizes the function names + input/output shapes in one place — every
// page imports from here so a function rename hits compile errors everywhere
// it's wrong.

import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from './firebase';

export type CategoryId =
  | 'adl'
  | 'self_care_independence'
  | 'client_rights'
  | 'legal_ethical'
  | 'basic_nursing'
  | 'health_care_team'
  | 'emotional_mental'
  | 'communication'
  | 'spiritual_cultural';

export type AnswerIndex = 0 | 1 | 2 | 3;
export type Difficulty = 1 | 2 | 3 | 4 | 5;
export interface ActiveSessionSummary { sessionId: string; status: 'active'; mode: 'exam' | 'practice' | 'revision' | 'trial'; category: string | null; answeredCount: number; questionCount: number; startedAtMs: number | null; updatedAtMs: number | null; expiresAtMs: number | null }
export const callGetActiveSessions = wrap<Record<string, never>, { schemaVersion: number; sessions: ActiveSessionSummary[] }>('getActiveSessions');

export const callCompleteOnboarding = wrap<{
  displayName: string; phoneNumber: string; state: string; targetExamDate?: string;
  termsAccepted: true; privacyAcknowledged: true; professionalDisclaimerAccepted: true;
  appVersion?: string; timeZone?: string;
}, { complete: true; journeyState: string; diagnosticRequired: boolean }>('completeOnboarding');

export interface DiagnosticQuestion { id: string; questionId: string; position: number; category: CategoryId; objectiveId: string; exposureFamilyId: string; difficulty: Difficulty; stem: string; options: [string, string, string, string] }
export interface DiagnosticState { exists?: boolean; sessionId: string; status: 'in_progress' | 'completed'; totalQuestions: number; answeredQuestionIds: string[]; questions: DiagnosticQuestion[]; resultSummary: DiagnosticResult | null }
export interface DiagnosticResult { overallScore: number; totalCorrect: number; totalQuestions: number; categories: Record<CategoryId, { correct: number; attempted: number }>; recommendation: { categoryId: CategoryId; objectiveId: string; reasonCode: string; suggestedDifficulty: Difficulty; suggestedQuestionCount: number; confidence: number } | null; notice: string }
export const callStartDiagnosticAssessment = wrap<{ requestId: string }, DiagnosticState>('startDiagnosticAssessment');
export const callGetDiagnosticState = wrap<{ sessionId?: string }, DiagnosticState | { exists: false }>('getDiagnosticState');
export const callSubmitDiagnosticAnswer = wrap<{ sessionId: string; questionId: string; selectedIndex: AnswerIndex; eventId: string; responseTimeMs?: number }, { accepted: true; duplicate: boolean; answeredCount: number }>('submitDiagnosticAnswer');
export const callFinishDiagnosticAssessment = wrap<{ sessionId: string; requestId: string }, { completed: true; resultSummary: DiagnosticResult }>('finishDiagnosticAssessment');

// ---------- Exam ----------

export interface QuestionForClient {
  id: string;
  category: CategoryId;
  difficulty: Difficulty;
  stem: string;
  options: [string, string, string, string];
}

export interface StartExamResult {
  sessionId: string;
  questions: QuestionForClient[];
  warnings: string[];
}

export const callStartExam = wrap<
  { mode: 'exam' | 'practice' | 'revision'; category?: CategoryId; examFormatId?: 'nnaap-2024-written-simulation-v1' | 'nnaap-2024-scored-practice-v1' | 'reviewed-mixed-practice-v1'; requestId: string },
  StartExamResult
>('startExam');

export interface ContentReadiness {
  checkedAtMs: number;
  eligibleTotal: number;
  exam: { requested: number; available: number; enabled: boolean };
  mixedPractice: { requested: number; available: number; enabled: boolean };
  revision: { requested: number; available: number; enabled: boolean };
  categories: Record<CategoryId, { requested: number; available: number; enabled: boolean }>;
}
export const callGetContentReadiness = wrap<Record<string, never>, ContentReadiness>('getContentReadiness');

export const callStartTrial = wrap<Record<string, never>, StartExamResult>('startTrial');

export interface SessionState {
  sessionId: string;
  mode: 'exam' | 'practice' | 'revision' | 'trial';
  startedAtMs?: number | null;
  questions: QuestionForClient[];
  completedQuestionIds: string[];
  isCompleted: boolean;
}

export const callGetSessionState = wrap<{ sessionId: string }, SessionState>('getSessionState');
export const callCancelExam = wrap<{ sessionId: string }, { cancelled?: boolean; alreadyDone?: boolean }>('cancelExam');

export const callBootstrapUserProfile = wrap<Record<string, never>, { created: boolean }>(
  'bootstrapUserProfile',
);

export const callSendVerificationEmail = wrap<
  Record<string, never>,
  { sent: boolean; alreadyVerified: boolean }
>('sendVerificationEmail');

export const callGetPublicStats = wrap<
  Record<string, never>,
  { totalQuestions: number; nnaapCategoryCount: number }
>('getPublicStats');

// ---------- Billing ----------

export const callCreateCheckoutSession = wrap<Record<string, never>, { url: string }>(
  'createCheckoutSession',
);
export const callCreatePortalSession = wrap<Record<string, never>, { url: string }>(
  'createPortalSession',
);
export const callRedeemAccessCode = wrap<
  { code: string; idempotencyKey: string },
  { status: 'applied'; productId: string; planId: string; accessEndAtMs: number }
>('redeemAccessCode');

export interface AdminCoupon {
  id: string; version: number; label: string; codeHint: string; benefitType: 'free_access';
  productId: 'full_access'; planId: string; durationDays: number | null; accessEndAtMs: number | null;
  startsAtMs: number; expiresAtMs: number; totalLimit: number; perUserLimit: number;
  active: boolean; redemptionCount: number; assignedUids: string[]; assignedEmailHints: string[];
  notes: string; createdAtMs: number | null; createdBy: string; updatedAtMs: number | null; updatedBy: string;
}

export type AdminCouponInput = {
  label: string; code?: string; generateCode?: boolean; benefitType: 'free_access'; productId: 'full_access';
  planId: string; durationDays: number | null; accessEndAtMs: number | null; startsAtMs: number; expiresAtMs: number;
  totalLimit: number; perUserLimit: number; active: boolean; assignedUids: string[]; assignedEmails: string[]; notes: string;
};
export const callAdminCreateCoupon = wrap<AdminCouponInput, { coupon: AdminCoupon; code: string }>('adminCreateCoupon');
export const callAdminUpdateCoupon = wrap<AdminCouponInput & { couponId: string; preserveAssignments?: boolean }, { coupon: AdminCoupon }>('adminUpdateCoupon');
export const callAdminListCoupons = wrap<Record<string, never>, { coupons: AdminCoupon[]; histories: Record<string, unknown[]>; codes: Record<string, string | null> }>('adminListCoupons');
export const callAdminRotateCouponCode = wrap<{ couponId: string; code?: string; generateCode?: boolean }, { coupon: AdminCoupon; code: string }>('adminRotateCouponCode');

// ---------- Admin: student performance monitor ----------
export interface StudentPerformance {
  uid: string;
  profile: { email: string; displayName: string | null; state: string | null; role: string; createdAtMs: number | null; subscriptionStatus: string | null; accessEndAtMs: number | null };
  totals: { questionsAnswered: number; correct: number; accuracy: number | null; sessionsStarted: number; sessionsCompleted: number; testsCompleted: number; revisionsCompleted: number; avgTestScore: number | null; lastActiveMs: number | null; streakDays: number; activeDays30: number };
  trend: { last7: { attempts: number; accuracy: number | null }; prior7: { attempts: number; accuracy: number | null } };
  byCategory: Array<{ category: CategoryId; attempts: number; correct: number; accuracy: number | null; state: string }>;
  activity: Array<{ dayMs: number; attempts: number; correct: number }>;
  objectives: Array<{ id: string; label: string; category: CategoryId; state: string; attempts: number; correct: number; accuracy: number | null }>;
  weakest: Array<{ id: string; label: string; category: CategoryId; accuracy: number | null; attempts: number }>;
  sessions: Array<{ id: string; mode: 'exam' | 'practice' | 'revision' | 'trial'; status: string; startedAtMs: number | null; completedAtMs: number | null; durationMs: number | null; score: number | null; correct: number | null; total: number | null; questionCount: number; answeredCount: number; category: string | null }>;
  testScores: Array<{ sessionId: string; completedAtMs: number; score: number; correct: number; total: number }>;
}
export const callAdminGetStudentPerformance = wrap<{ uid: string }, StudentPerformance>('adminGetStudentPerformance');

export type CancelReason =
  | 'too_expensive'
  | 'taking_exam_soon'
  | 'not_enough_content'
  | 'technical_issues'
  | 'found_another_tool'
  | 'just_pausing'
  | 'other';

export const callUserCancelSubscription = wrap<
  { reason: CancelReason; comment?: string },
  { ok: boolean; mode: 'at_period_end' }
>('userCancelSubscription');

export const callApplyWinbackDiscount = wrap<
  Record<string, never>,
  { ok: boolean; couponId: string }
>('applyWinbackDiscount');

export interface SubmitAnswerResult {
  correct?: boolean;
  rationale?: string;
  correctCanonicalIndex?: AnswerIndex;
  correctShuffledIndex?: AnswerIndex;
  selectedCanonicalIndex?: AnswerIndex;
  acknowledged?: boolean;
  coachReason?: string | null;
  nextQuestionId?: string | null;
}

export const callSubmitAnswer = wrap<
  { sessionId: string; questionId: string; selectedShuffledIndex: AnswerIndex; flagged?: boolean },
  SubmitAnswerResult
>('submitAnswer');

export interface ReviewItem {
  questionId: string;
  category: CategoryId;
  difficulty: Difficulty;
  stem: string;
  optionsShuffled: [string, string, string, string];
  correctShuffledIndex: AnswerIndex;
  selectedShuffledIndex: AnswerIndex | null;
  correct: boolean;
  flagged: boolean;
  rationale: string;
  optionExplanations: [string, string, string, string];
}

export interface ReadinessCategoryStat {
  accuracy: number;
  rawAccuracy: number;
  sampleSize: number;
  weight: number;
  contribution: number;
}

export interface ReadinessReport {
  score: number;
  byCategory: Partial<Record<CategoryId, ReadinessCategoryStat>>;
  totalAnswered: number;
  totalCorrect: number;
}

export interface ReadinessHeadline {
  bracket: 'low' | 'mid' | 'high' | 'peak';
  headline: string;
  body: string;
}

export interface FinishExamResult {
  sessionId: string;
  mode?: 'exam' | 'practice' | 'revision' | 'trial';
  score: number;
  correct: number;
  total: number;
  categoryBreakdown: Partial<Record<CategoryId, { correct: number; total: number }>>;
  review: ReviewItem[];
  readinessReport?: ReadinessReport;
  readinessHeadline?: ReadinessHeadline;
  sessionCoach?: { improvement: string | null; concern: string | null; nextAction: string };
}

export const callFinishExam = wrap<{ sessionId: string }, FinishExamResult>('finishExam');

// ---------- Admin: content bank ----------

export const callUploadContentBankItem = wrap<
  { filename: string; text: string; kind?: 'questions' | 'answers' | 'reference' | 'mixed' },
  { itemId: string; alreadyExisted: boolean; sizeBytes: number }
>('uploadContentBankItem');

export const callDeleteContentBankItem = wrap<{ itemId: string }, { ok: true }>(
  'deleteContentBankItem',
);

export const callResetContentBankConsumption = wrap<
  { itemIds?: string[] },
  { ok: true; resetCount: number }
>('resetContentBankConsumption');

export const callReanalyzeContentBankItem = wrap<
  { itemId: string },
  { ok: true; analysisId: string }
>('reanalyzeContentBankItem');

// ---------- Admin: generation ----------

export const callGenerateQuestions = wrap<
  {
    count?: number;
    model?: string;
    validationModel?: string;
    focusCategory?: CategoryId;
    includeConsumed?: boolean;
    sourceItemIds?: string[];
    autoApproveHighQuality?: boolean;
    skipDuplicates?: boolean;
    enforceReadingLevel?: boolean;
    categoryDistribution?: Partial<Record<CategoryId, number>>;
    difficultyDistribution?: Partial<Record<'easy' | 'medium' | 'hard', number>>;
    sourceTypeDistribution?: Partial<Record<'concept' | 'procedure' | 'scenario', number>>;
  },
  {
    jobId: string;
    status: 'completed' | 'partial' | 'failed';
    generated: number;
    failed: number;
    skipped: number;
    candidateIds: string[];
    byCategory: Partial<Record<CategoryId, number>>;
    sourcesUsed: number;
    sourcesDropped: number;
    knowledgeStats: {
      concepts: number;
      procedures: number;
      scenarios: number;
      terminology: number;
    };
    tokenUsage: { input: number; output: number };
    summaryReport?: {
      averageValidationScore: number;
      totalTokensUsed: number;
      totalCost: number;
    };
  }
>('generateQuestions', { timeout: 540_000 });

// ---------- Admin: question moderation ----------

export const callApproveQuestion = wrap<{ questionId: string }, { ok: true }>('approveQuestion');
export const callRejectQuestion = wrap<{ questionId: string; reason?: string }, { ok: true }>(
  'rejectQuestion',
);
export const callRetireQuestion = wrap<{ questionId: string; reason: string }, { ok: true }>(
  'retireQuestion',
);

export type EditQuestionPatch = {
  stem?: string;
  options?: [string, string, string, string];
  correctAnswerIndex?: AnswerIndex;
  rationale?: string;
  optionExplanations?: [string, string, string, string];
  difficulty?: Difficulty;
  keywordTags?: string[];
};

export const callEditDraftQuestion = wrap<
  { questionId: string; patch: EditQuestionPatch },
  { ok: true }
>('editDraftQuestion');

// ---------- Admin: Firebase-native brain pipeline ----------

export type SourceFileType = 'txt' | 'pdf';
export type SourceType =
  | 'textbook'
  | 'exam'
  | 'handbook'
  | 'policy'
  | 'notes'
  | 'mixed'
  | 'other';

export const callCreateSourceUpload = wrap<
  {
    filename: string;
    file_type: SourceFileType;
    source_type?: SourceType;
    region?: string | null;
    size: number;
    sha256: string;
  },
  { sourceId: string; storagePath: string }
>('createSourceUpload');

export const callQueueSourceIngest = wrap<{ sourceId: string }, { ok: true }>(
  'queueSourceIngest',
);

export const callReprocessAllSources = wrap<Record<string, never>, { ok: true; queued: number }>(
  'reprocessAllSources',
);

export const callImportLegacyContentBank = wrap<
  Record<string, never>,
  { ok: true; imported: number; skipped: number }
>('importLegacyContentBank');

export const callRunDigestJob = wrap<
  { sourceId?: string },
  { ok: true; units: number; candidates: number; created: number; merged: number }
>('runDigestJob', { timeout: 540_000 });

export interface BrainQueryResult {
  id: string;
  type: 'concept' | 'procedure' | 'scenario' | 'term' | 'red_flag';
  canonical_text: string;
  support_count: number;
  citations: string[];
  sources: string[];
  confidence: number;
  category: CategoryId;
  difficulty: Difficulty;
  score: number;
}

export const callBrainQuery = wrap<
  { category?: CategoryId | null; difficulty?: Difficulty | null; topic?: string | null; limit?: number },
  { results: BrainQueryResult[] }
>('brainQuery');

export type ItemType = 'recall' | 'scenario' | 'procedure' | 'red_flag' | 'priority';
export type BloomLevel = 'remember' | 'understand' | 'apply' | 'analyze';

export const callGenerateGroundedQuestions = wrap<
  {
    count?: number;
    category?: CategoryId | null;
    difficulty?: Difficulty | null;
    topic?: string | null;
    autoApprove?: boolean;
    item_type?: ItemType | null;
    bloom_level?: BloomLevel | null;
  },
  { ok: true; jobId: string; generatedQuestionIds: string[] }
>('generateGroundedQuestions', { timeout: 540_000 });

export interface PipelineSettings {
  mode: 'monitor' | 'automatic';
  auto_digest: boolean;
  auto_repair_failed_chunks: boolean;
  auto_generate_drafts: boolean;
  auto_approve_generated: boolean;
  qa_auto_approve_threshold: number;
  failed_chunk_retry_limit: number;
  generation_batch_size: number;
  min_facts_per_category: number;
  min_sample_questions: number;
  min_live_questions_per_category: number;
}

export const callUpdatePipelineSettings = wrap<
  Partial<PipelineSettings>,
  { ok: true; settings: PipelineSettings }
>('updatePipelineSettings');

export const callApproveGeneratedQuestion = wrap<{ questionId: string }, { ok: true }>(
  'approveGeneratedQuestion',
);

export interface CategoryCoverage {
  category: CategoryId;
  facts: number;
  samples: number;
  drafts: number;
  live: number;
  target: number;
  gap: number;
  coverage_pct: number;
}

export interface UnderCitedFact {
  fact_id: string;
  canonical_text: string;
  category: CategoryId;
  difficulty: Difficulty;
  citation_count: number;
}

export interface CoverageReport {
  generated_at: number;
  target_per_category: number;
  totals: { facts: number; samples: number; drafts: number; live: number };
  categories: CategoryCoverage[];
  under_cited_facts: UnderCitedFact[];
  weakest_category: CategoryId | null;
}

export const callGetCoverageReport = wrap<Record<string, never>, CoverageReport>(
  'getCoverageReport',
  { timeout: 60_000 },
);

export const callRejectGeneratedQuestion = wrap<
  { questionId: string; reason?: string },
  { ok: true }
>('rejectGeneratedQuestion');

// ---------- helpers ----------

function wrap<I, O>(
  name: string,
  options?: { timeout?: number },
): (data: I) => Promise<O> {
  const fn = httpsCallable<I, O>(functions, name, options);
  return async (data) => {
    const r: HttpsCallableResult<O> = await fn(data);
    return r.data;
  };
}

export const callGetMasterySummary = wrap<Record<string, never>, {
  policyVersion: string; taxonomyVersion: string; taxonomyStatus: 'proposed'; notice: string;
  recordedAttempts: number; excludedAttempts: number;
  categories: Array<{ category: import('@shared/categories').CategoryId; state: string; requiredObjectives: number; assessedObjectives: number; nextObjectiveId: string | null }>;
  objectives: Array<{ id: string; label: string; state: string; independentCorrect: number; independentAttempts: number; sessions: number; latestAt: number | null }>;
}>('getMasterySummary');
